import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchClientsList,
  fetchClientDetails,
  createClient,
  updateClient,
  deleteClient,
  updateClientStage,
  fetchClientHealthMetrics,
  fetchAllClientHealthMetrics,
  fetchClientActivityFeed,
  fetchClientBookings,
  fetchClientContacts,
  fetchClientInvoices,
} from '@/services/clientService';
import type { ClientFullData, ClientListData, ClientStage, ClientHealthMetrics, ClientActivityFeedItem, ActivityEventType } from '@/types/client';
import { useAuth } from '@/context/AuthContext';
import Logger from '@/utils/logger';

const HOOK_COMPONENT = 'useClients';

export const clientQueryKeys = {
  all: ['clients'] as const,
  lists: () => [...clientQueryKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...clientQueryKeys.lists(), filters] as const, // Example for future filtering
  details: () => [...clientQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientQueryKeys.details(), id] as const,
  healthMetrics: () => [...clientQueryKeys.all, 'health'] as const,
  healthMetric: (id: string) => [...clientQueryKeys.healthMetrics(), id] as const,
  activityFeed: (id: string, filters?: { eventTypes?: ActivityEventType[] }) =>
    [...clientQueryKeys.all, 'activity', id, filters] as const,
  bookings: (id: string) => [...clientQueryKeys.all, 'bookings', id] as const,
  contacts: (id: string) => [...clientQueryKeys.all, 'contacts', id] as const,
  invoices: (id: string) => [...clientQueryKeys.all, 'invoices', id] as const,
};

/**
 * Hook to fetch a list of clients.
 */
export const useFetchClientsList = () => {
  return useQuery<ClientListData[], Error>({
    queryKey: clientQueryKeys.lists(),
    queryFn: fetchClientsList,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch details for a single client.
 * @param clientId - The ID of the client to fetch. Enabled only if clientId is provided.
 */
export const useFetchClientDetails = (clientId: string | null) => {
  return useQuery<ClientFullData | null, Error>({
    queryKey: clientQueryKeys.detail(clientId!),
    queryFn: () => fetchClientDetails(clientId!),
    enabled: !!clientId, // Only run query if clientId is not null
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to create a new client.
 */
export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<ClientFullData, Error, Partial<ClientFullData>>({
    mutationFn: (clientData: Partial<ClientFullData>) => {
      if (!user?.id) {
        Logger.error('User not authenticated for createClient', undefined, { component: HOOK_COMPONENT });
        return Promise.reject(new Error('User not authenticated'));
      }
      return createClient(clientData);
    },
    onSuccess: (data) => {
      // Invalidate the list of clients to refetch
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.lists() });
      // Optionally, set the data for the new client's detail query
      queryClient.setQueryData(clientQueryKeys.detail(data.id!), data);
    },
    onError: (error) => {
       Logger.error('Error creating client', error, { component: HOOK_COMPONENT });
    }
  });
};

/**
 * Hook to update an existing client.
 */
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation<ClientFullData, Error, { clientId: string; clientData: Partial<ClientFullData> }>({
    mutationFn: ({ clientId, clientData }) => updateClient(clientId, clientData),
    onSuccess: (data, variables) => {
      // Invalidate the list of clients
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.lists() });
      // Invalidate or update the specific client's details
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.detail(variables.clientId) });
      // queryClient.setQueryData(clientQueryKeys.detail(variables.clientId), data); // Alternative: directly set data
    },
    onError: (error, variables) => {
       Logger.error('Error updating client', error, { component: HOOK_COMPONENT, clientId: variables.clientId });
    }
  });
};

/**
 * Hook to delete a client.
 */
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: (_, clientId) => {
      // Invalidate the list of clients
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.lists() });
      // Remove the specific client's details from cache
      queryClient.removeQueries({ queryKey: clientQueryKeys.detail(clientId) });
    },
    onError: (error, clientId) => {
       Logger.error('Error deleting client', error, { component: HOOK_COMPONENT, clientId });
    }
  });
};

/**
 * Hook to fetch health metrics for a single client.
 */
export const useFetchClientHealthMetrics = (clientId: string | null) => {
  return useQuery<ClientHealthMetrics | null, Error>({
    queryKey: clientQueryKeys.healthMetric(clientId!),
    queryFn: () => fetchClientHealthMetrics(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch health metrics for all clients.
 */
export const useFetchAllClientHealthMetrics = () => {
  return useQuery<ClientHealthMetrics[], Error>({
    queryKey: clientQueryKeys.healthMetrics(),
    queryFn: fetchAllClientHealthMetrics,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch the activity feed for a client.
 */
export const useFetchClientActivityFeed = (
  clientId: string | null,
  options?: { eventTypes?: ActivityEventType[]; limit?: number }
) => {
  return useQuery<ClientActivityFeedItem[], Error>({
    queryKey: clientQueryKeys.activityFeed(clientId!, { eventTypes: options?.eventTypes }),
    queryFn: () => fetchClientActivityFeed(clientId!, options),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2,
  });
};

/**
 * Hook to fetch bookings for a client.
 */
export const useFetchClientBookings = (clientId: string | null) => {
  return useQuery({
    queryKey: clientQueryKeys.bookings(clientId!),
    queryFn: () => fetchClientBookings(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch contacts linked to a client.
 */
export const useFetchClientContacts = (clientId: string | null) => {
  return useQuery({
    queryKey: clientQueryKeys.contacts(clientId!),
    queryFn: () => fetchClientContacts(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch invoices for a client.
 */
export const useFetchClientInvoices = (clientId: string | null) => {
  return useQuery({
    queryKey: clientQueryKeys.invoices(clientId!),
    queryFn: () => fetchClientInvoices(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to update a client's pipeline stage.
 */
export const useUpdateClientStage = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { clientId: string; stage: ClientStage }>({
    mutationFn: ({ clientId, stage }) => updateClientStage(clientId, stage),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.detail(clientId) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.activityFeed(clientId) });
    },
  });
};
